import { useState, useRef, useCallback, useMemo } from 'react';
import { getVerseText, getBookInfo, formatReference } from '../utils/bibleData';

/**
 * Custom hook for managing batch recording functionality
 * Handles verse-by-verse recording with individual audio files
 * Provides instant UI response with background file processing
 */
export const useBatchRecording = ({
  selectedBook,
  selectedChapter,
  startVerse,
  endVerse,
  currentUser,
  onVerseComplete,
  onRecordingComplete,
  onRecordingModeReset
}) => {
  // Core recording state
  const [isRecording, setIsRecording] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [audioBlob, setAudioBlob] = useState(null);
  const [currentVerseIndex, setCurrentVerseIndex] = useState(0);
  const [chosenMimeType, setChosenMimeType] = useState('');
  const [audioDuration, setAudioDuration] = useState(0);

  // Verse management state
  const [verseAudioURLs, setVerseAudioURLs] = useState({});

  // Verses currently being encoded/uploaded (verseNumber -> true). Lets the
  // summary modal open instantly and show a spinner per in-flight verse.
  const [pendingVerseSaves, setPendingVerseSaves] = useState({});

  // Re-record state (for summary modal inline re-recording)
  const [reRecordingVerse, setReRecordingVerse] = useState(null);
  const [reRecordTime, setReRecordTime] = useState(0);
  const [reRecordProcessing, setReRecordProcessing] = useState(null);

  // Refs for recording management
  const mediaRecorderRef = useRef(null);
  const isRedoingRef = useRef(false);
  const reRecordMediaRecorderRef = useRef(null);
  const reRecordStreamRef = useRef(null);
  const reRecordIntervalRef = useRef(null);
  const reRecordChunksRef = useRef([]);
  const isNextVerseRef = useRef(false);
  const isReviewRef = useRef(false);
  const isPauseRef = useRef(false);
  const currentVerseNumberRef = useRef(null);
  const currentAudioBlobRef = useRef(null);
  const recordingIntervalRef = useRef(null);
  const autoSaveIntervalRef = useRef(null);
  // Recording formats that claimed isTypeSupported() but whose encoder failed
  // at runtime (e.g. Chrome's MP4/AAC after certain updates). Session-scoped.
  const badMimeTypesRef = useRef(new Set());
  // Live chunk buffer of the active recorder, so the review watchdog can
  // salvage audio if the recorder wedges and onstop never fires.
  const activeChunksRef = useRef([]);
  // Timer that force-opens the review modal if onstop never arrives.
  const reviewWatchdogRef = useRef(null);
  // Pre-warmed microphone stream, opened during the countdown so the first
  // verse isn't captured on a cold mic (avoids verse-1 cut-off/glitches).
  const micStreamRef = useRef(null);

  // Microphone constraints shared by warm-up and recording.
  // Disable browser DSP (echo cancellation, noise suppression, auto-gain) —
  // these degrade voice clarity for intentional studio-style Bible readings.
  const getMicConstraints = () => ({
    audio: {
      echoCancellation: false,
      noiseSuppression: false,
      autoGainControl: false,
      sampleRate: { ideal: 44100 },
      channelCount: { ideal: 1 }
    }
  });

  // Open and start capturing from the mic ahead of recording so auto-gain /
  // echo-cancellation settle before the first verse is recorded.
  const warmUpMic = useCallback(async () => {
    try {
      const existing = micStreamRef.current;
      if (existing && existing.getAudioTracks().some(t => t.readyState === 'live')) {
        return existing;
      }
      const stream = await navigator.mediaDevices.getUserMedia(getMicConstraints());
      micStreamRef.current = stream;
      console.log('🎙️ Mic warmed up (pre-roll before first verse)');
      return stream;
    } catch (e) {
      console.warn('🎙️ Mic warm-up failed; will open on record:', e);
      return null;
    }
  }, []);

  // Helper function to get verse reference
  const getVerseReference = useCallback((verseNumber, bookId = selectedBook, chapter = selectedChapter) => {
    const book = getBookInfo(bookId);
    return formatReference(book?.name || 'Unknown', chapter, verseNumber);
  }, [selectedBook, selectedChapter]);

  // Helper function to get audio duration from blob.
  // Uses an Audio element (not decodeAudioData) so it reliably handles fragmented
  // MP4 produced by iOS Safari's MediaRecorder, which decodeAudioData cannot decode.
  const getAudioDuration = useCallback((blob) => new Promise((resolve) => {
    const url = URL.createObjectURL(blob);
    const audio = new Audio();
    audio.onloadedmetadata = () => {
      URL.revokeObjectURL(url);
      resolve(isFinite(audio.duration) ? audio.duration : 0);
    };
    audio.onerror = () => {
      URL.revokeObjectURL(url);
      resolve(0);
    };
    audio.src = url;
  }), []);

  // Save current verse file immediately
  const saveCurrentVerseFile = useCallback(async (blobToSave = null, verseNumber = null) => {
    const blob = blobToSave || audioBlob;
    const verse = verseNumber || (startVerse + currentVerseIndex);
    const verseReference = getVerseReference(verse, selectedBook, selectedChapter);
    
    if (!blob || !currentUser || blob.size === 0) {
      // A 0-byte blob means the recorder was stopped before it had encoded
      // any audio at all (e.g. stop() fired within the first fraction of a
      // second of start()) — uploading it would silently create a file with
      // nothing in it, which the review player can never show a duration or
      // play back for. Skip the upload instead of saving a ghost file.
      if (blob && blob.size === 0) {
        console.error(`❌ SAVE FAILED: Recording for verse ${verse} (${verseReference}) captured 0 bytes of audio — not uploading. Re-record this verse.`);
      } else {
        console.warn(`❌ SAVE FAILED: Missing blob or currentUser for verse ${verse} (${verseReference})`);
      }
      // Clear any optimistic spinner for this verse so it can't spin forever
      setPendingVerseSaves(prev => {
        if (!prev[verse]) return prev;
        const next = { ...prev };
        delete next[verse];
        return next;
      });
      return;
    }

    console.log(`📝 VERSE FILE START: Beginning save process for verse ${verse} (${verseReference})`);
    console.log(`📝 VERSE FILE INFO: Blob size: ${blob.size} bytes, type: ${blob.type}`);

    setPendingVerseSaves(prev => (prev[verse] ? prev : { ...prev, [verse]: true }));

    try {
      // Upload the current audio blob directly as the verse file
      const { ref, uploadBytes, getDownloadURL } = await import('firebase/storage');
      const { storage } = await import('../config/firebase');
      
      const timestamp = Date.now();

      // Upload the raw browser-encoded blob directly — no client-side
      // decode/re-encode. decodeAudioData (used by the old WAV-conversion
      // step) can decode a live-captured MP4/AAC recording and report the
      // CORRECT duration while silently corrupting/silencing audio near the
      // end — a duration check can't catch that because the length matches;
      // only the content is wrong. This is the same fix already applied to
      // Letter Box recordings, which had the identical failure mode.
      const audioDuration = await getAudioDuration(blob);
      const uploadBlob = blob;
      const ext = blob.type.includes('mp4') || blob.type.includes('m4a') ? 'm4a'
        : blob.type.includes('mpeg') ? 'mp3'
        : blob.type.includes('ogg') ? 'ogg'
        : 'webm';
      console.log(`🎵 Uploading raw blob (${ext}), duration ${audioDuration.toFixed(2)}s`);

      const filename = `verse-${verse}-${timestamp}.${ext}`;
      const storageRef = ref(storage, `recordings/${currentUser.uid}/verses/${filename}`);

      console.log(`📤 UPLOAD START: Uploading ${filename} to Firebase Storage for verse ${verse} (${verseReference})`);

      await uploadBytes(storageRef, uploadBlob, {
        contentType: uploadBlob.type || 'audio/webm',
        customMetadata: {
          verseNumber: verse.toString(),
          duration: audioDuration.toString(),
          originalRecording: 'true'
        }
      });
      
      const downloadURL = await getDownloadURL(storageRef);
      
      const verseFile = {
        verseNumber: verse,
        filename,
        downloadURL,
        startTime: 0,
        endTime: audioDuration,
        duration: audioDuration,
        size: uploadBlob.size
      };
      
      // Update the verseAudioURLs with the new file
      setVerseAudioURLs(prev => {
        const newState = {
          ...prev,
          [`verse-${verse}`]: verseFile
        };
        console.log(`📋 TEMPORARY STORAGE: Verse ${verse} (${verseReference}) added to temporary storage`);
        console.log(`📋 TEMPORARY STORAGE: Total verses in temp storage: ${Object.keys(newState).length}`);
        console.log(`📋 TEMPORARY STORAGE: Current verses: ${Object.keys(newState).map(k => k.replace('verse-', '')).join(', ')}`);
        return newState;
      });
      
      console.log(`✅ UPLOAD SUCCESS: Verse ${verse} (${verseReference}) uploaded successfully to Firebase Storage`);
      console.log(`✅ UPLOAD SUCCESS: File: ${filename}, Size: ${uploadBlob.size} bytes, Duration: ${audioDuration}s`);
      
      // Reset audioBlob after saving so next verse starts fresh
      setAudioBlob(null);
      
      // Call the completion callback
      if (onVerseComplete) {
        onVerseComplete(verse, verseFile);
      }
      
    } catch (error) {
      console.error('❌ Error saving verse file:', error);
    } finally {
      setPendingVerseSaves(prev => {
        if (!prev[verse]) return prev;
        const next = { ...prev };
        delete next[verse];
        return next;
      });
    }
  }, [audioBlob, startVerse, currentVerseIndex, selectedBook, selectedChapter, currentUser, getVerseReference, getAudioDuration, onVerseComplete]);

  // Start recording for a specific verse
  const startRecording = useCallback(async (verseIndex = null) => {
    try {
      const actualVerseIndex = verseIndex !== null ? verseIndex : currentVerseIndex;
      const currentVerseNumber = startVerse + actualVerseIndex;
      const verseReference = getVerseReference(currentVerseNumber, selectedBook, selectedChapter);
      console.log('🎙️ Starting recording...', { currentVerseIndex: actualVerseIndex, isRecording, isPaused });
      console.log(`🎙️ RECORDING START: Beginning recording for verse ${currentVerseNumber} (${verseReference})`);
      
      // Don't reset the next verse flag here - let the onstop handler process it first
      
      // Set the current verse number ref for this recording
      currentVerseNumberRef.current = currentVerseNumber;
      
      // Use ONE persistent mic stream for the whole session. Reusing the
      // already-open stream makes verse-to-verse transitions near-instant
      // (no getUserMedia cold-start that drops the first second of speech).
      // The stream is only released on review/cleanup.
      let stream = micStreamRef.current;
      if (!stream || !stream.getAudioTracks().some(t => t.readyState === 'live')) {
        console.log('🎙️ Opening mic stream');
        stream = await navigator.mediaDevices.getUserMedia(getMicConstraints());
        micStreamRef.current = stream; // keep alive across verses
      } else {
        console.log('🎙️ Reusing live mic stream (instant)');
      }
      console.log('🎙️ Audio stream ready:', stream.getAudioTracks()[0]?.getSettings());
      
      let recorderOptions = {};

      // Prefer MP4/AAC: Chrome and iOS Safari both support it, and the resulting
      // fMP4 decodes cleanly via decodeAudioData → WAV. WebM/Opus is the fallback
      // for Firefox; Chrome's live-stream WebM is unreliable with decodeAudioData.
      const preferTypes = [
        'audio/mp4;codecs=mp4a.40.2', // Chrome + iOS Safari AAC
        'audio/mp4',                   // MP4 fallback
        'audio/webm;codecs=opus',      // Firefox
        'audio/webm',                  // generic WebM fallback
        'audio/mpeg',                  // last resort
      ];

      // isTypeSupported() can LIE: Chrome may report MP4/AAC as supported and
      // then throw "EncodingError: Encoder initialization failed" the moment
      // recording starts, producing a 0-byte recording that silently self-stops
      // (observed after a Chrome update). Formats that fail at runtime get
      // blacklisted for the session and recording auto-retries with the next.
      const chosenType = preferTypes
        .filter(t => !badMimeTypesRef.current.has(t))
        .find(t => MediaRecorder.isTypeSupported(t)) || '';
      setChosenMimeType(chosenType);
      if (chosenType) {
        recorderOptions.mimeType = chosenType;
        console.log('🎙️ Using MIME type:', chosenType);
      } else {
        console.log('🎙️ No preferred MIME type available, using browser default');
      }

      mediaRecorderRef.current = new MediaRecorder(stream, recorderOptions);

      let encoderFailed = false;
      mediaRecorderRef.current.onerror = (event) => {
        const err = event.error || event;
        console.error('🚨 MediaRecorder error:', err);
        if (chosenType && !badMimeTypesRef.current.has(chosenType)) {
          encoderFailed = true;
          badMimeTypesRef.current.add(chosenType);
          console.warn(`🚨 Blacklisting broken encoder "${chosenType}" for this session; retrying with next format`);
        }
      };

      const chunks = [];
      activeChunksRef.current = chunks; // expose to the review watchdog
      mediaRecorderRef.current.ondataavailable = (event) => {
        console.log('🎙️ Data available:', event.data.type, event.data.size);
        chunks.push(event.data);
      };
      
      mediaRecorderRef.current.onstop = async () => {
        console.log('🎙️ Recording stopped, chunks:', chunks.length);

        // onstop arrived — the recorder isn't wedged, so the review watchdog
        // can stand down (the normal review path below handles the modal).
        if (reviewWatchdogRef.current) {
          clearTimeout(reviewWatchdogRef.current);
          reviewWatchdogRef.current = null;
        }

        // Use the recorder's actual MIME type (not the preferred type, which may
        // not have been used if the browser didn't support it).
        const blobType = mediaRecorderRef.current?.mimeType || chosenType || 'audio/webm';
        const newBlob = new Blob(chunks, { type: blobType });

        console.log('🎵 New recording completed:', {
          blobSize: newBlob.size,
          blobType: newBlob.type,
          chosenType: chosenType,
          chunksCount: chunks.length
        });

        // A broken encoder produces an instant self-stop with 0 bytes. Don't
        // treat that as a recording — restart automatically with the failed
        // format blacklisted so the user's take isn't lost or saved as garbage.
        if (encoderFailed && newBlob.size === 0) {
          console.warn(`🔁 Encoder "${chosenType}" produced 0 bytes — restarting recording for verse ${currentVerseNumber} with next format`);
          setTimeout(() => startRecording(actualVerseIndex), 100);
          return;
        }

        // SIMPLIFIED: Just set the new blob (one file per verse)
        currentAudioBlobRef.current = newBlob;
        setAudioBlob(newBlob);
        const duration = await getAudioDuration(newBlob);
        setAudioDuration(duration);

        // NOTE: the mic stream is intentionally NOT stopped here — it stays
        // live across verses so the next verse can start instantly. It is
        // released on review (below) and on cleanup/unmount.

        // Show modal when recording is complete (not when moving to next verse)
        console.log(`🔍 ONSTOP DEBUG: isNextVerseRef.current = ${isNextVerseRef.current}, isReviewRef.current = ${isReviewRef.current}, isPauseRef.current = ${isPauseRef.current}, isRedoingRef.current = ${isRedoingRef.current}`);
        if (isReviewRef.current) {
          // Done recording this batch — release the mic now.
          stream.getTracks().forEach(track => track.stop());
          if (micStreamRef.current === stream) micStreamRef.current = null;
          // Review action: save THIS recorder's verse. The modal is ALREADY
          // open (handleReviewClick opens it instantly with a spinner) — this
          // save resolves the spinner card into the real verse card. Use the
          // verse number captured when this recorder started — NOT the shared
          // ref, which may already point at the next verse.
          const verseReference = getVerseReference(currentVerseNumber, selectedBook, selectedChapter);
          console.log(`💾 REVIEW SAVE: Processing verse ${currentVerseNumber} (${verseReference}) for upload...`);
          setTimeout(async () => {
            await saveCurrentVerseFile(newBlob, currentVerseNumber);
            isReviewRef.current = false; // Reset flag
          }, 100);
        } else if (isNextVerseRef.current) {
          // Save THIS recorder's verse (captured at start), not the shared ref
          // which has already advanced to the next verse.
          const verseNumber = currentVerseNumber;
          const verseReference = getVerseReference(verseNumber, selectedBook, selectedChapter);
          console.log(`💾 ONSTOP SAVE: Processing verse ${verseNumber} (${verseReference}) for upload...`);
          setTimeout(async () => {
            await saveCurrentVerseFile(newBlob, verseNumber);
            isNextVerseRef.current = false; // Reset flag after processing
          }, 100);
        } else if (isPauseRef.current) {
          // This is a pause action - do NOT save, just pause
          console.log(`⏸️ PAUSE: Recording paused - verse NOT saved`);
          isPauseRef.current = false; // Reset flag
        } else if (isRedoingRef.current) {
          // This is a redo action - do NOT save the verse, just restart
          console.log(`🔄 REDO: Recording stopped - verse NOT saved, restarting fresh`);
          // No save, no modal - just restart
        } else {
          // This is some other action - do NOT save the verse
          console.log(`❓ OTHER: Recording stopped - verse NOT saved`);
          // Recording isn't continuing, so release the mic.
          stream.getTracks().forEach(track => track.stop());
          if (micStreamRef.current === stream) micStreamRef.current = null;
        }
      };
      
      console.log('🎙️ Starting MediaRecorder with options:', recorderOptions);
      // 1s timeslice: iOS Safari's MediaRecorder, started with no timeslice,
      // only flushes encoded audio once at stop() — and that single final
      // flush can come back completely empty (confirmed on-device: every
      // verse uploaded as 0 bytes). Periodic chunks mean the real audio is
      // already safely collected as it's recorded, so even an empty final
      // flush only risks losing the last fraction of a second.
      mediaRecorderRef.current.start(1000);
      setIsRecording(true);
      // SIMPLIFIED: Always reset recording time for each verse
      setRecordingTime(0);
      
      // Start recording timer
      recordingIntervalRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);
      
    } catch (error) {
      console.error('Error starting recording:', error);
      alert('Unable to access microphone. Please check permissions.');
    }
  }, [currentVerseIndex, startVerse, selectedBook, selectedChapter, isRecording, isPaused, getVerseReference, getAudioDuration, saveCurrentVerseFile]);

  // Pause recording
  const pauseRecording = useCallback(() => {
    if (mediaRecorderRef.current && isRecording && !isPaused) {
      const currentVerseNumber = startVerse + currentVerseIndex;
      console.log(`⏸️ PAUSE: Pausing recording for verse ${currentVerseNumber} (Genesis 1:${currentVerseNumber})`);

      // INSTANT UI feedback — same pattern as Next Verse.
      setIsPaused(true);
      if (recordingIntervalRef.current) {
        clearInterval(recordingIntervalRef.current);
      }

      const recorder = mediaRecorderRef.current;
      // Delay the actual browser-level pause() call, same grace period and
      // same reason as Next Verse: the encoder has some internal buffering,
      // so audio spoken right before the tap may not be flushed yet.
      // MediaRecorder.pause() halts capture the instant it's called — with
      // no delay, that trailing word/sound is lost for good.
      setTimeout(() => {
        if (recorder.state === 'recording') {
          recorder.pause();

          // Save current progress to local storage when pausing
          console.log('🔍 Pause debug:', {
            hasAudioBlob: !!audioBlob,
            audioBlobSize: audioBlob?.size,
            currentVerseNumber
          });

          if (audioBlob) {
            // Save the current audio as a verse file when pausing.
            // Signature is (blobToSave, verseNumber) — these were swapped,
            // which silently produced a garbage save ("verse [object Blob]")
            // and lost the paused verse's audio entirely.
            console.log(`💾 Saving current audio for verse ${currentVerseNumber}...`);
            saveCurrentVerseFile(audioBlob, currentVerseNumber);
          } else {
            console.log('⚠️ No audioBlob to save when pausing');
          }
        } else {
          console.log(`⏸️ PAUSE: MediaRecorder state is '${recorder.state}', cannot pause`);
        }
      }, 500);
    }
  }, [mediaRecorderRef, isRecording, isPaused, startVerse, currentVerseIndex, audioBlob]);

  // Resume recording
  const resumeRecording = useCallback(() => {
    if (mediaRecorderRef.current && isPaused) {
      const currentVerseNumber = startVerse + currentVerseIndex;
      console.log(`▶️ RESUME: Resuming recording for verse ${currentVerseNumber} (Genesis 1:${currentVerseNumber})`);
      
      // Check MediaRecorder state before resuming
      if (mediaRecorderRef.current.state === 'paused') {
        // Use MediaRecorder's built-in resume method
        mediaRecorderRef.current.resume();
        setIsPaused(false);
        
        // Restart the recording timer
        recordingIntervalRef.current = setInterval(() => {
          setRecordingTime(prev => prev + 1);
        }, 1000);
      } else {
        console.log(`▶️ RESUME: MediaRecorder state is '${mediaRecorderRef.current.state}', cannot resume`);
        // If MediaRecorder is not paused, just update the UI state
        setIsPaused(false);
        // Restart the recording timer
        recordingIntervalRef.current = setInterval(() => {
          setRecordingTime(prev => prev + 1);
        }, 1000);
      }
    }
  }, [mediaRecorderRef, isPaused, startVerse, currentVerseIndex]);

  // Stop recording
  const stopRecording = useCallback((verseIndex = null) => {
    if (mediaRecorderRef.current && (isRecording || isPaused)) {
      const actualVerseIndex = verseIndex !== null ? verseIndex : currentVerseIndex;
      const currentVerseNumber = startVerse + actualVerseIndex;
      const verseReference = getVerseReference(currentVerseNumber, selectedBook, selectedChapter);
      console.log(`⏹️ RECORDING STOP: Stopping recording for verse ${currentVerseNumber} (${verseReference})`);
      
      // Set flag to indicate this is a pause (not next verse)
      isNextVerseRef.current = false;

      // Resume-before-stop: Chrome can wedge when stopping a paused recorder.
      try { if (mediaRecorderRef.current.state === 'paused') mediaRecorderRef.current.resume(); } catch (_) {}
      try { mediaRecorderRef.current.stop(); } catch (_) {}
      setIsRecording(false);
      setIsPaused(false);
      
      if (recordingIntervalRef.current) {
        clearInterval(recordingIntervalRef.current);
      }
      
      // Reset recording time when stopping
      setRecordingTime(0);
      
      // Pause does NOT save the verse - just stops recording
      // The onstop handler will NOT save or show modal for pause
    }
  }, [mediaRecorderRef, isRecording, isPaused, currentVerseIndex, startVerse, selectedBook, selectedChapter, getVerseReference]);

  // ── In-modal per-verse re-recording ─────────────────────────────────────────

  const startReRecord = useCallback(async (verseNumber) => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia(getMicConstraints());
      reRecordStreamRef.current = stream;

      const preferTypes = [
        'audio/mp4;codecs=mp4a.40.2',
        'audio/mp4',
        'audio/webm;codecs=opus',
        'audio/webm',
        'audio/mpeg',
      ];
      // Skip formats whose encoder already failed at runtime this session.
      const mimeType = preferTypes
        .filter(t => !badMimeTypesRef.current.has(t))
        .find(t => MediaRecorder.isTypeSupported(t)) || '';

      const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
      reRecordChunksRef.current = [];
      recorder.ondataavailable = (e) => { if (e.data.size > 0) reRecordChunksRef.current.push(e.data); };
      reRecordMediaRecorderRef.current = recorder;
      // 1s timeslice — see startRecording: iOS Safari's stop()-only final
      // flush can come back empty; periodic chunks capture the real audio
      // as it's recorded instead of relying on that single flush.
      recorder.start(1000);

      setReRecordingVerse(verseNumber);
      setReRecordTime(0);
      reRecordIntervalRef.current = setInterval(() => setReRecordTime(t => t + 1), 1000);
    } catch (e) {
      console.error('Re-record mic error:', e);
      alert('Could not access microphone. Please check permissions and try again.');
    }
  }, []);

  const stopReRecord = useCallback(async () => {
    const verseNumber = reRecordingVerse;
    if (!verseNumber || !reRecordMediaRecorderRef.current) return;

    clearInterval(reRecordIntervalRef.current);
    setReRecordingVerse(null);
    setReRecordProcessing(verseNumber);

    await new Promise((resolve) => {
      reRecordMediaRecorderRef.current.onstop = async () => {
        try {
          const blob = new Blob(reRecordChunksRef.current, {
            type: reRecordMediaRecorderRef.current.mimeType || 'audio/webm',
          });
          reRecordStreamRef.current?.getTracks().forEach(t => t.stop());
          reRecordStreamRef.current = null;
          await saveCurrentVerseFile(blob, verseNumber);
        } catch (e) {
          console.error('Re-record save error:', e);
        } finally {
          setReRecordProcessing(null);
          setReRecordTime(0);
          resolve();
        }
      };
      reRecordMediaRecorderRef.current.stop();
    });
  }, [reRecordingVerse, saveCurrentVerseFile]);

  // Restart current verse recording
  const restartCurrentVerse = useCallback(async () => {
    if (isRecording || isPaused) {
      const currentVerseNumber = startVerse + currentVerseIndex;
      console.log(`🔄 REDO: Restarting recording for verse ${currentVerseNumber} (Genesis 1:${currentVerseNumber})`);
      
      // Set flag to indicate this is a redo action (don't save the current recording)
      isRedoingRef.current = true;

      // Stop current recording (resume first if paused — Chrome wedge guard)
      if (mediaRecorderRef.current) {
        try { if (mediaRecorderRef.current.state === 'paused') mediaRecorderRef.current.resume(); } catch (_) {}
        try { mediaRecorderRef.current.stop(); } catch (_) {}
      }
      setIsRecording(false);
      setIsPaused(false);
      
      if (recordingIntervalRef.current) {
        clearInterval(recordingIntervalRef.current);
      }
      
      // Reset everything and start fresh
      setAudioBlob(null);
      setRecordingTime(0);
      
      // Start recording again
      setTimeout(() => {
        isRedoingRef.current = false; // Reset flag
        startRecording();
      }, 100);
    }
  }, [isRecording, isPaused, startVerse, currentVerseIndex, startRecording]);

  // Move to next verse
  const nextVerse = useCallback((e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    
    console.log(`🔍 NEXT VERSE CLICKED: currentVerseIndex=${currentVerseIndex}, endVerse=${endVerse}, startVerse=${startVerse}`);
    console.log(`🔍 NEXT VERSE STATE: isRecording=${isRecording}, isPaused=${isPaused}, audioBlob=${!!audioBlob}`);
    
    try {
      if (currentVerseIndex < (endVerse - startVerse)) {
        // Save the current verse number before changing the index
        const currentVerseNumber = startVerse + currentVerseIndex;
        currentVerseNumberRef.current = currentVerseNumber;
        
        // Get verse reference for logging
        const verseReference = getVerseReference(currentVerseNumber, selectedBook, selectedChapter);
        console.log(`📖 NEXT VERSE: Moving from verse ${currentVerseNumber} (${verseReference}) to next verse`);
        
        // INSTANT UI UPDATE - Move to next verse immediately
        const newIndex = currentVerseIndex + 1;
        setCurrentVerseIndex(newIndex);
        
        // INSTANT UI UPDATE - Reset recording state immediately
        setRecordingTime(0);
        
        // SAVE CURRENT VERSE - Save the completed verse to local storage
        if (audioBlob) {
          // TODO: Implement local storage save
          console.log('💾 Saving current verse to local storage...');
        }
        
        // BACKGROUND PROCESSING - Handle current recording save and new recording start
        if (isRecording || isPaused) {
          console.log(`🔍 NEXT VERSE: Currently recording/paused, stopping and saving current verse`);
          // Set flag to prevent modal from opening
          isNextVerseRef.current = true;

          // Stop current recording — but not instantly. DSP (echo
          // cancellation/noise suppression) is OFF here, but the encoder
          // still has some internal buffering, so audio spoken right before
          // the tap can be captured but not yet flushed. Stopping
          // immediately risked truncating that trailing word; this grace
          // period lets it land before we cut. The verse index/timer
          // already advanced above, so the UI feels just as instant.
          const recorderToStop = mediaRecorderRef.current;
          const tapTime = performance.now();
          const trackAtTap = recorderToStop?.stream?.getAudioTracks?.()[0];
          console.log(`🔬 NEXT DIAGNOSTIC: verse=${currentVerseNumber}, recorder.state=${recorderToStop?.state}, track.readyState=${trackAtTap?.readyState}, track.muted=${trackAtTap?.muted}`);
          setIsRecording(false);
          setIsPaused(false);
          if (recordingIntervalRef.current) {
            clearInterval(recordingIntervalRef.current);
          }

          setTimeout(() => {
            const elapsed = performance.now() - tapTime;
            const sameRecorder = recorderToStop === mediaRecorderRef.current;
            const trackNow = recorderToStop?.stream?.getAudioTracks?.()[0];
            console.log(`🔬 NEXT DIAGNOSTIC (delayed): verse=${currentVerseNumber}, elapsedMs=${elapsed.toFixed(0)}, sameRecorderRef=${sameRecorder}, recorder.state=${recorderToStop?.state}, track.readyState=${trackNow?.readyState}, track.muted=${trackNow?.muted}`);
            if (recorderToStop) {
              // Resume first if paused — Chrome can wedge (no onstop, no
              // data) when stopping a paused recorder.
              try { if (recorderToStop.state === 'paused') recorderToStop.resume(); } catch (_) {}
              try { recorderToStop.requestData(); } catch (_) {}
              try { recorderToStop.stop(); } catch (_) {}
            }

            // Start the next verse almost immediately after — the mic stream
            // stays live, so there's no cold-start. A tiny delay lets stop() settle.
            setTimeout(() => {
              const newVerseNumber = startVerse + newIndex;
              currentVerseNumberRef.current = newVerseNumber;
              console.log(`🔍 NEXT VERSE: Starting new recording for verse ${newVerseNumber} (index ${newIndex})`);
              startRecording(newIndex);
            }, 20);
          }, 500);
        } else {
          // No current recording, just start the new one
          console.log(`🔍 NEXT VERSE: No current recording, starting new recording for verse ${startVerse + newIndex}`);
          setTimeout(() => {
            const newVerseNumber = startVerse + newIndex;
            currentVerseNumberRef.current = newVerseNumber;
            startRecording(newIndex);
          }, 50); // Even faster for non-recording state
        }
      }
    } catch (error) {
      console.error('Error in nextVerse:', error);
    }
  }, [currentVerseIndex, endVerse, startVerse, selectedBook, selectedChapter, getVerseReference, audioBlob, isRecording, isPaused, startRecording]);

  // Move to previous verse
  const previousVerse = useCallback((e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    if (currentVerseIndex > 0) {
      // INSTANT UI UPDATE - Move to previous verse immediately
      const newIndex = currentVerseIndex - 1;
      setCurrentVerseIndex(newIndex);
    }
  }, [currentVerseIndex]);

  // Handle review click
  const handleReviewClick = useCallback(async () => {
    // If currently recording or paused, save the current verse first
    if (isRecording || isPaused) {
      // Use the verse number ref that was set when recording started (most reliable)
      const currentVerseNumber = currentVerseNumberRef.current || (startVerse + currentVerseIndex);
      console.log(`💾 REVIEW SAVE: Saving current verse ${currentVerseNumber} before showing modal...`);
      console.log(`💾 REVIEW SAVE: startVerse=${startVerse}, currentVerseIndex=${currentVerseIndex}, ref=${currentVerseNumberRef.current}`);
      
      // Set flag to indicate this is a review action (not pause)
      isReviewRef.current = true;

      // INSTANT FEEDBACK: show the spinner for the in-flight verse and open
      // the modal right away; the save streams in when it finishes.
      setPendingVerseSaves(prev => (prev[currentVerseNumber] ? prev : { ...prev, [currentVerseNumber]: true }));
      if (onRecordingModeReset) {
        onRecordingModeReset();
      }
      if (onRecordingComplete) {
        onRecordingComplete();
      }

      const rec = mediaRecorderRef.current;
      if (rec && rec.state !== 'inactive') {
        // Chrome can wedge (onstop never fires, data never flushes) when a
        // PAUSED recorder is stopped. Resume first, ask for the buffered
        // data explicitly, then stop — belt and suspenders.
        try { if (rec.state === 'paused') rec.resume(); } catch (e) { console.warn('resume-before-stop failed:', e); }
        try { rec.requestData(); } catch (e) { console.warn('requestData failed:', e); }
        try { rec.stop(); } catch (e) { console.warn('recorder stop failed:', e); }
        setIsRecording(false);
        setIsPaused(false);

        if (recordingIntervalRef.current) {
          clearInterval(recordingIntervalRef.current);
        }

        // Watchdog: if onstop STILL never fires, salvage whatever audio was
        // flushed into the chunk buffer, save it, and open the modal anyway.
        // The modal must never silently fail to appear.
        reviewWatchdogRef.current = setTimeout(async () => {
          if (!isReviewRef.current) return; // onstop handled it
          isReviewRef.current = false;
          console.warn('⏱️ REVIEW WATCHDOG: recorder never fired onstop — salvaging buffered audio and opening the modal');
          try { rec.onstop = null; rec.ondataavailable = null; } catch (_) {}
          try {
            micStreamRef.current?.getTracks().forEach(t => t.stop());
            micStreamRef.current = null;
          } catch (_) {}
          const salvaged = new Blob(activeChunksRef.current || [], { type: rec.mimeType || 'audio/webm' });
          if (salvaged.size > 0) {
            await saveCurrentVerseFile(salvaged, currentVerseNumber);
          } else {
            console.warn('⏱️ REVIEW WATCHDOG: no buffered audio to salvage — verse was lost');
            setPendingVerseSaves(prev => {
              if (!prev[currentVerseNumber]) return prev;
              const next = { ...prev };
              delete next[currentVerseNumber];
              return next;
            });
          }
        }, 2500);
      } else {
        // Recorder is already dead (e.g. encoder failure self-stop) — no
        // onstop will ever come. Save the last completed blob if it exists
        // and was never saved.
        setIsRecording(false);
        setIsPaused(false);
        if (recordingIntervalRef.current) {
          clearInterval(recordingIntervalRef.current);
        }
        isReviewRef.current = false;
        const pending = currentAudioBlobRef.current;
        if (pending && pending.size > 0 && !verseAudioURLs[`verse-${currentVerseNumber}`]) {
          console.log(`💾 REVIEW: recorder already inactive — saving last completed blob for verse ${currentVerseNumber}`);
          await saveCurrentVerseFile(pending, currentVerseNumber);
        } else {
          setPendingVerseSaves(prev => {
            if (!prev[currentVerseNumber]) return prev;
            const next = { ...prev };
            delete next[currentVerseNumber];
            return next;
          });
        }
      }

      // The modal is already open; onstop (or the watchdog) saves the verse
      // and its spinner card resolves into the real verse card.
    } else {
      // No current recording, but save any existing audio blob to local storage
      if (audioBlob) {
        // TODO: Implement local storage save
        console.log('💾 Saving draft to local storage...');
      }
      // Reset recording mode and call the completion callback to show modal
      if (onRecordingModeReset) {
        onRecordingModeReset();
      }
      if (onRecordingComplete) {
        onRecordingComplete();
      }
    }
  }, [isRecording, isPaused, startVerse, currentVerseIndex, audioBlob, verseAudioURLs, saveCurrentVerseFile, onRecordingComplete, onRecordingModeReset]);

  // Computed values
  const currentVerseNumber = useMemo(() => {
    const maxIndex = endVerse - startVerse;
    const boundedIndex = Math.min(currentVerseIndex, maxIndex);
    return startVerse + boundedIndex;
  }, [startVerse, currentVerseIndex, endVerse]);
  const totalVerses = useMemo(() => endVerse - startVerse + 1, [endVerse, startVerse]);
  const progressPercentage = useMemo(() => {
    const percentage = ((currentVerseIndex + 1) / totalVerses) * 100;
    console.log(`📊 PROGRESS DEBUG: currentVerseIndex=${currentVerseIndex}, totalVerses=${totalVerses}, startVerse=${startVerse}, endVerse=${endVerse}, percentage=${percentage}`);
    return Math.min(percentage, 100); // Cap at 100% to prevent overflow
  }, [currentVerseIndex, totalVerses, startVerse, endVerse]);
  
  // Review button visibility - NEW LOGIC: Only show when paused and has verses
  const showReviewButton = useMemo(() => {
    const hasVerses = Object.keys(verseAudioURLs).length > 0;
    console.log('🔍 Review button debug:', {
      isPaused,
      verseAudioURLs: Object.keys(verseAudioURLs),
      hasVerses,
      showReviewButton: isPaused && hasVerses
    });
    return isPaused && hasVerses;
  }, [isPaused, verseAudioURLs]);

  // Format time helper
  const formatTime = useCallback((seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    // Limit to 2 decimal places for display
    const secsFormatted = secs.toFixed(2);
    return `${mins}:${secsFormatted.padStart(5, '0')}`;
  }, []);

  // Get verse preview text
  const getVersePreview = useCallback((book, chapter, verse, maxLength = 80) => {
    const fullText = getVerseText(book, chapter, verse);
    if (fullText.length <= maxLength) return fullText;
    return fullText.substring(0, maxLength) + '...';
  }, []);

  // Cleanup function
  const cleanup = useCallback(() => {
    if (recordingIntervalRef.current) {
      clearInterval(recordingIntervalRef.current);
    }
    if (autoSaveIntervalRef.current) {
      clearInterval(autoSaveIntervalRef.current);
    }
    if (reviewWatchdogRef.current) {
      clearTimeout(reviewWatchdogRef.current);
      reviewWatchdogRef.current = null;
    }
    if (mediaRecorderRef.current) {
      try { mediaRecorderRef.current.stop(); } catch (_) {}
    }
    // Release a warmed-but-unused mic stream so the mic indicator turns off.
    if (micStreamRef.current) {
      micStreamRef.current.getTracks().forEach(track => track.stop());
      micStreamRef.current = null;
    }
    if (reRecordIntervalRef.current) clearInterval(reRecordIntervalRef.current);
    if (reRecordStreamRef.current) {
      reRecordStreamRef.current.getTracks().forEach(t => t.stop());
      reRecordStreamRef.current = null;
    }
    if (reRecordMediaRecorderRef.current) {
      try { reRecordMediaRecorderRef.current.stop(); } catch (_) {
        // ignore if already stopped
      }
    }
  }, []);

  return {
    // Recording state
    isRecording,
    isPaused,
    recordingTime,
    audioBlob,
    currentVerseIndex,
    chosenMimeType,
    audioDuration,
    
    // Verse management
    verseAudioURLs,
    pendingVerseSaves,
    currentVerseNumber,
    totalVerses,
    progressPercentage,
    
    // UI state
    showReviewButton,
    
    // Recording controls
    warmUpMic,
    startRecording,
    pauseRecording,
    resumeRecording,
    stopRecording,
    restartCurrentVerse,
    
    // Verse navigation
    nextVerse,
    previousVerse,
    
    // Review functionality
    handleReviewClick,
    
    // In-modal re-record
    reRecordingVerse,
    reRecordTime,
    reRecordProcessing,
    startReRecord,
    stopReRecord,

    // Utility functions
    formatTime,
    getVersePreview,
    saveCurrentVerseFile,
    
    // State setters
    setAudioBlob,
    setRecordingTime,
    setCurrentVerseIndex,
    setVerseAudioURLs,
    setIsRecording,
    setIsPaused,
    
    // Cleanup
    cleanup
  };
};

export default useBatchRecording;
